// DGA Design System — Token Mirror (JS)
// Values must stay in sync with dga-tokens.scss.
// Use for programmatic access (e.g. Vuetify theme config, unit tests, gallery).
// NOTE: Brand color keys (primary, primaryTonal, secondary, titleInk) were removed
// in Phase A — they are now theme-driven via CSS vars (--dga-color-primary etc.).
// Read --dga-color-* at runtime or use the active Vuetify theme object for brand colors.

// Colour palette — "Color Style Guide - Variables" (New Latest Design, 2026-10-08). Every colour below is
// picked from these scales; pages use the semantic names (brand / tokens.color / tone), not the raw steps.
// Contrast notes (white text on the step): neon-600 4.7:1 is the lightest neon that passes AA, neon-300 needs
// dark ink (brand-900, 10.2:1), pan-purple-600 6.9:1, magenta-600 6.4:1 (magenta-500 fails at 4.0:1).
export const palette = {
  brand: {
    50: '#EDF6FF', 100: '#D0DAE9', 200: '#B3BFD1', 300: '#97A5B9', 400: '#7C8BA2', 500: '#60738C',
    600: '#475B76', 700: '#31435D', 800: '#1D2E45', 900: '#0B192C', 950: '#00020F',
  },
  neonBlue: {
    50: '#AEFFFF', 100: '#91F4FF', 200: '#63E6FF', 300: '#00D7FF', 400: '#00B8E6', 500: '#0099D0',
    600: '#0079B7', 700: '#005996', 800: '#003972', 900: '#001A4C', 950: '#00002C',
  },
  panPurple: {
    50: '#E5F0FF', 100: '#CAD6FF', 200: '#AFBBFF', 300: '#94A0FF', 400: '#7B85F9', 500: '#6366F1',
    600: '#4A3FDB', 700: '#3613B9', 800: '#250092', 900: '#180068', 950: '#100044',
  },
  magentaAffair: {
    50: '#FFE4FF', 100: '#EFCBFF', 200: '#DCB0FF', 300: '#CA95FF', 400: '#B878FD', 500: '#A855F7',
    600: '#8C19DE', 700: '#6D00B9', 800: '#4E0090', 900: '#300064', 950: '#17003E',
  },
};

// Brand colours — THE single source for the Vuetify themes (plugins/vuetify.js builds dgaTheme and the
// white-label `light` theme from this) and for JS consumers that need a hex (Highcharts series, default
// widget colours saved to the DB). CSS reads them through --dga-color-primary etc. (dga-tokens.scss).
//
// Values follow the "New Latest Design" files (chat page, landing, admin v2) built on the 4 key colours of the
// style guide: brand-900 #0B192C (actions), neon-300 #00D7FF (highlight / icons), pan-purple-500 #6366F1 and
// magenta-500 #A855F7 (gradient / accent). Status colours (green / red / orange / teal) are the design's own
// values — the palette has no scale for them. Some default values do NOT pass WCAG AA (as drawn in the design);
// the accessible set `tokensAA` below replaces them when the viewer picks a display mode in
// A11yDisplayControls (dga-tokens.scss: html.a11y-mode-gray / html.a11y-mode-contrast).
export const brand = {
  primary:   palette.brand[900],   // buttons, toggles, search, stat icon tiles
  secondary: palette.brand[900],
  error:     '#DC3545',            // trash icon, error text, error pills
  info:      '#007C96',            // selected nav / tab text, info pills, icon tiles
  success:   '#4CAF50',            // SAVE / EXPORT ALL buttons
  warning:   '#FA8C00',
};

// "Light.tokens.json" (Figma variables, 2026-10-12) — the colour set of the admin pages (portal + SuperAdmin =
// Vuetify dgaTheme). Landing / chat (light theme) keep `brand` / `tokens` above. Mirror: --dga-light-* in dga-tokens.scss.
export const lightTokens = {
  brand:   { deepBlue: '#0A34A2', cyan: '#0081E6', violet: '#B126E3', violetEnd: '#AB55F7' },
  text:    { primary: '#0B192C', primaryBlue: '#0A34A2', body: '#334155', secondary: '#64748B', accent: '#0081E6',
             violet: '#B126E3', onDark: '#FBFAFC' },
  surface: { canvas: '#FBFAFC', subtle: '#F0F8FC', selected: '#E5F8FC', violet: '#F1EDFC' },
  border:  { default: '#DCE5EE', accent: '#00A4C4' },
};

// Admin brand (dgaTheme in plugins/vuetify.js): buttons / switches / search / icon tiles = brand/deep-blue
// (white 10.4:1). Status colours (error / info / success / warning) are not in Light.tokens.json → same as `brand`.
export const brandAdmin = {
  ...brand,
  primary:   lightTokens.brand.deepBlue,
  secondary: lightTokens.text.primary,
};

export const tokens = {
  palette,
  brand,
  // Brand gradient of the chat design (send button, login pill): neon-300 → magenta-500
  // brand/violet-end #AB55F7 (New Landing N Chat Design 2026-10-12; was magenta-500 #A855F7)
  gradientBrand: `linear-gradient(90deg, ${palette.neonBlue[300]} 0%, #AB55F7 100%)`,
  // Chat welcome greeting (text gradient): purple → blue
  gradientGreeting: 'linear-gradient(90deg, #6A40AD 0%, #28659F 100%)',

  color: {
    // Semantic (mirror of dgaTheme in plugins/vuetify.js)
    error:           brand.error,
    success:         brand.success,
    warning:         brand.warning,
    info:            brand.info,

    // Surfaces — white + the neutral off-white of the design (page, sidebar, search box)
    surface:         '#FFFFFF',
    surfaceMuted:    '#FBFAFC',
    background:      '#FBFAFC',
    sidebar:         '#FBFAFC',

    // Text / Ink
    ink:             palette.brand[900],
    inkMuted:        '#64748B',
    inkNav:          '#334155',   // sidebar menu, dialog subtitle, pager
    heading:         '#424242',   // admin page title
    inkIndigo:       '#16134A',   // landing agencies section (HOME PAGE DESIGN-02)
    inkDisabled:     'rgba(0, 0, 0, 0.45)',
    placeholder:     '#AAAAAA',
    icon:            palette.neonBlue[300],

    // Selected / active state (nav item, tab, info pill, icon tile)
    selectedBg:      '#E5F8FC',
    selectedInk:     '#007C96',
    selectedLine:    '#00A4C4',   // active tab underline, outlined accent card
    navActiveBg:     '#E5F8FC',   // active sidebar item (= selectedBg; admin overrides)

    // Borders / Divider
    border:          '#DCE5EE',
    borderStrong:    '#787878',   // search / filter field outline
    fieldBorder:     '#EEEEEE',   // empty field inside a form dialog
    divider:         'rgba(11, 25, 44, 0.10)',
    dividerStrong:   'rgba(11, 25, 44, 0.12)',
    pagerActive:     '#DEE2E6',

    // Highlight — the bright neon of the landing / chat design (white text as drawn)
    highlight:       palette.neonBlue[300],
    onHighlight:     '#FFFFFF',
    highlightSoft:   '#E5F8FC',

    // Accent
    accent:          palette.panPurple[500],
    accentSoft:      palette.panPurple[50],
    accentStrong:    palette.magentaAffair[500],

    // Solid status fills (role / state badges)
    dangerSolid:     '#FE4B4B',
    slateSolid:      '#64748B',
  },

  // Status tones: { ink, bg } — design values
  tone: {
    success: { ink: '#198754', bg: '#DFF1E0' },
    warning: { ink: '#FA8C00', bg: '#FEF1E0' },
    caution: { ink: '#FFC107', bg: '#FFF3DE' },
    error:   { ink: '#DC3545', bg: '#F7DADA' },
    info:    { ink: '#007C96', bg: '#E5F8FC' },
    neutral: { ink: '#334155', bg: '#DEE2E6' },
    accent:  { ink: palette.panPurple[600], bg: palette.panPurple[50] },
  },

  // Chart series — the admin Dashboard design (bar = [0], pie = all five)
  chart: ['#5C6BC0', '#1E88E5', '#43A047', '#FFA000', '#D81B60'],

  typography: {
    fontFamily:       "'IBM Plex Sans Thai', 'Noto Sans Thai', sans-serif",
    fontSizeXs:       '0.75rem',    // 12px
    fontSizeSm:       '0.875rem',   // 14px
    fontSizeMd:       '1rem',       // 16px
    fontSizeLg:       '1.25rem',    // 20px
    fontSizeXl:       '1.5rem',     // 24px
    fontWeightRegular: 400,
    fontWeightMedium:  500,
    fontWeightBold:    700,
  },

  radius: {
    sm:   '8px',
    md:   '12px',
    lg:   '16px',
    xl:   '20px',
    pill: '9999px',
  },

  shadow: {
    e1:     '0 2px 6px rgba(0, 0, 0, 0.12)',
    e2:     '0 4px 12px rgba(0, 0, 0, 0.10)',
    dialog: '0 24px 48px rgba(0, 0, 0, 0.12)',
  },

  spacing: {
    space1: '4px',
    space2: '8px',
    space3: '12px',
    space4: '16px',
    space5: '20px',
    space6: '24px',
    space7: '28px',
    space8: '32px',
  },

  breakpoint: {
    xs:  0,
    sm:  600,
    md:  960,
    lg:  1280,
    xl:  1920,
  },
};

// Admin overrides of `tokens` (Light.tokens.json) — CSS: the `.v-theme--dgaTheme` block in dga-tokens.scss, which also
// re-colours the CSS-only tokens (info card, row edit icon, switch thumb). Keys not listed here keep the `tokens` value.
export const tokensAdmin = {
  gradientBrand: `linear-gradient(90deg, ${lightTokens.brand.cyan} 0%, ${lightTokens.brand.violetEnd} 100%)`,
  color: {
    heading:       lightTokens.text.primary,     // page title (was #424242)
    placeholder:   lightTokens.text.secondary,   // 4.8:1 (was #AAAAAA)
    icon:          lightTokens.brand.cyan,
    selectedInk:   lightTokens.text.primary,     // active nav / tab text 16.1:1 on selected (was teal #007C96)
    navActiveBg:   '#C8D1EA',                    // active sidebar item = document icon tile (primary-tonal 12% twice)
    fieldBorder:   lightTokens.border.default,
    highlight:     lightTokens.brand.cyan,
    accent:        lightTokens.brand.violet,
    accentSoft:    lightTokens.surface.violet,
    accentStrong:  lightTokens.brand.violetEnd,
  },
  tone: {
    info:    { ink: lightTokens.text.primary, bg: lightTokens.surface.selected },
    accent:  { ink: lightTokens.text.violet,  bg: lightTokens.surface.violet },
  },
};

// Accessible replacements (WCAG AA) for the default values above that fall short — applied by CSS when the viewer
// picks a display mode (html.a11y-mode-gray / html.a11y-mode-contrast, see dga-tokens.scss). JS consumers that draw
// colours themselves (Highcharts) keep the default set.
export const tokensAA = {
  brand: { success: '#1E7A4C', warning: '#B45309', info: palette.neonBlue[700] },
  color: {
    icon:         palette.neonBlue[600],   // 4.7:1
    selectedInk:  palette.neonBlue[700],   // 6.7:1 on selectedBg
    selectedLine: palette.neonBlue[600],
    placeholder:  '#64748B',
    fieldBorder:  '#787878',               // 4.4:1 (non-text needs 3:1)
    onHighlight:  palette.brand[900],      // 10.2:1 on neon-300
    dangerSolid:  '#C62828',               // white 5.6:1
    slateSolid:   palette.brand[600],      // white 6.9:1
  },
  gradientBrand: `linear-gradient(90deg, ${palette.neonBlue[700]} 0%, ${palette.magentaAffair[600]} 100%)`,
  tone: {
    success: { ink: '#146C43' },
    warning: { ink: '#8A5300' },
    caution: { ink: '#8A5300' },
    error:   { ink: '#B42318' },
    info:    { ink: palette.neonBlue[700] },
  },
};
